import { useState } from 'react';
import {
  Settings as SettingsIcon,
  Bell,
  Github,
  Slack,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { useSettings, useUpdateSettings } from '@/hooks';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Badge,
  Skeleton,
  Input,
} from '@/components/ui';

export function SettingsPage() {
  const { data: settings, isLoading, error, refetch } = useSettings();
  const updateSettings = useUpdateSettings();
  const [hasChanges, setHasChanges] = useState(false);

  // Local form state
  const [formState, setFormState] = useState({
    timezone: '',
    refreshInterval: 30,
    autoRefresh: true,
    emailNotifications: true,
    slackNotifications: false,
    workflowSuccess: true,
    workflowFailure: true,
    dailyDigest: false,
  });

  // Update form state when settings are loaded
  useState(() => {
    if (settings) {
      setFormState({
        timezone: settings.general.timezone,
        refreshInterval: settings.general.refreshInterval,
        autoRefresh: settings.general.autoRefresh,
        emailNotifications: settings.notifications.email,
        slackNotifications: settings.notifications.slack,
        workflowSuccess: settings.notifications.workflowSuccess,
        workflowFailure: settings.notifications.workflowFailure,
        dailyDigest: settings.notifications.dailyDigest,
      });
    }
  });

  const handleSave = () => {
    updateSettings.mutate(
      {
        general: {
          timezone: formState.timezone,
          refreshInterval: formState.refreshInterval,
          autoRefresh: formState.autoRefresh,
          dateFormat: settings?.general.dateFormat ?? 'MMM d, yyyy',
        },
        notifications: {
          email: formState.emailNotifications,
          slack: formState.slackNotifications,
          workflowSuccess: formState.workflowSuccess,
          workflowFailure: formState.workflowFailure,
          dailyDigest: formState.dailyDigest,
        },
      },
      {
        onSuccess: () => {
          setHasChanges(false);
        },
      }
    );
  };

  const handleChange = <K extends keyof typeof formState>(
    key: K,
    value: (typeof formState)[K]
  ) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-foreground-muted mt-1">
            Configure your Mission Control preferences
          </p>
        </div>
        <Card variant="bordered">
          <CardContent className="py-12 text-center">
            <XCircle className="h-12 w-12 text-error mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground">
              Failed to load settings
            </h3>
            <p className="text-foreground-muted mt-1 mb-4">
              An error occurred while loading your settings.
            </p>
            <Button variant="secondary" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-foreground-muted mt-1">
            Configure your Mission Control preferences
          </p>
        </div>
        {hasChanges && (
          <Button onClick={handleSave} isLoading={updateSettings.isPending}>
            <Save className="h-4 w-4" />
            Save Changes
          </Button>
        )}
      </div>

      {/* General settings */}
      <Card variant="bordered">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-background-tertiary">
              <SettingsIcon className="h-5 w-5 text-foreground-muted" />
            </div>
            <div>
              <CardTitle>General</CardTitle>
              <CardDescription>
                Basic application settings and preferences
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Timezone
                  </label>
                  <select
                    value={formState.timezone || settings?.general.timezone}
                    onChange={(e) => handleChange('timezone', e.target.value)}
                    className="w-full h-10 rounded-lg border border-border bg-background-tertiary px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="Europe/London">London</option>
                    <option value="Europe/Paris">Paris</option>
                    <option value="Asia/Tokyo">Tokyo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Auto-refresh interval (seconds)
                  </label>
                  <Input
                    type="number"
                    min={10}
                    max={300}
                    value={formState.refreshInterval || settings?.general.refreshInterval}
                    onChange={(e) =>
                      handleChange('refreshInterval', parseInt(e.target.value, 10))
                    }
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="autoRefresh"
                  checked={formState.autoRefresh ?? settings?.general.autoRefresh}
                  onChange={(e) => handleChange('autoRefresh', e.target.checked)}
                  className="h-4 w-4 rounded border-border bg-background-tertiary text-accent focus:ring-accent"
                />
                <label htmlFor="autoRefresh" className="text-sm text-foreground">
                  Enable auto-refresh for dashboard and workflow pages
                </label>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card variant="bordered">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-background-tertiary">
              <Bell className="h-5 w-5 text-foreground-muted" />
            </div>
            <div>
              <CardTitle>Notifications</CardTitle>
              <CardDescription>
                Configure how you receive notifications
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Email notifications
                  </p>
                  <p className="text-xs text-foreground-muted">
                    Receive notifications via email
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={
                    formState.emailNotifications ?? settings?.notifications.email
                  }
                  onChange={(e) =>
                    handleChange('emailNotifications', e.target.checked)
                  }
                  className="h-4 w-4 rounded border-border bg-background-tertiary text-accent focus:ring-accent"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Workflow success alerts
                  </p>
                  <p className="text-xs text-foreground-muted">
                    Get notified when workflows complete successfully
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={
                    formState.workflowSuccess ?? settings?.notifications.workflowSuccess
                  }
                  onChange={(e) =>
                    handleChange('workflowSuccess', e.target.checked)
                  }
                  className="h-4 w-4 rounded border-border bg-background-tertiary text-accent focus:ring-accent"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Workflow failure alerts
                  </p>
                  <p className="text-xs text-foreground-muted">
                    Get notified when workflows fail
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={
                    formState.workflowFailure ?? settings?.notifications.workflowFailure
                  }
                  onChange={(e) =>
                    handleChange('workflowFailure', e.target.checked)
                  }
                  className="h-4 w-4 rounded border-border bg-background-tertiary text-accent focus:ring-accent"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-foreground">Daily digest</p>
                  <p className="text-xs text-foreground-muted">
                    Receive a daily summary of all activity
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={
                    formState.dailyDigest ?? settings?.notifications.dailyDigest
                  }
                  onChange={(e) => handleChange('dailyDigest', e.target.checked)}
                  className="h-4 w-4 rounded border-border bg-background-tertiary text-accent focus:ring-accent"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Integrations */}
      <Card variant="bordered">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-background-tertiary">
              <Github className="h-5 w-5 text-foreground-muted" />
            </div>
            <div>
              <CardTitle>Integrations</CardTitle>
              <CardDescription>
                Connect external services to Mission Control
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* GitHub integration */}
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div className="flex items-center gap-3">
                  <Github className="h-6 w-6 text-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">GitHub</p>
                    <p className="text-xs text-foreground-muted">
                      {settings?.integrations.github?.connected
                        ? `Connected as ${settings.integrations.github.username}`
                        : 'Connect your GitHub account'}
                    </p>
                  </div>
                </div>
                {settings?.integrations.github?.connected ? (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Connected
                  </Badge>
                ) : (
                  <Button variant="secondary" size="sm">
                    Connect
                  </Button>
                )}
              </div>

              {/* Slack integration */}
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <Slack className="h-6 w-6 text-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Slack</p>
                    <p className="text-xs text-foreground-muted">
                      {settings?.integrations.slack?.connected
                        ? `Connected to ${settings.integrations.slack.workspace}`
                        : 'Connect your Slack workspace'}
                    </p>
                  </div>
                </div>
                {settings?.integrations.slack?.connected ? (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Connected
                  </Badge>
                ) : (
                  <Button variant="secondary" size="sm">
                    Connect
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
